import { push } from "connected-react-router";
import last from "lodash/last";
import * as api from "../api";
import { file2Id, id2FileName } from "../lib/utils";
import { logException } from "../errorLogging";
import reactGA from "../reactGA";
import { getPrice } from "../reducers";

let mosaic = {};

export const init = () => dispatch => {
  api
    .getLocale()
    .then(locale => {
      dispatch({ type: "SET_LOCALE", locale });
    })
    .catch(error => {
      logException(error, { event: "Error in getting user location" });
    });
};

// %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%% Mosaic %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
export const initializeMosaicCreator = instance => dispatch => {
  mosaic = instance;

  mosaic.onTileError = (id, error) => {
    console.error(error);
    dispatch({ type: "ADD_MOSAIC_TILE_ERROR", id });
    dispatch(alert("warning", `Fail to read ${id2FileName(id)}.`));
    logException(error, { event: "Error in loading mosaic tiles" });
  };

  mosaic.onClearAll = () => {
    dispatch({ type: "CLEAR_MOSAIC_TILES_SUCCESS" });
  };

  mosaic.onProgress = progress => {
    dispatch({ type: "SET_MOSAIC_PROGRESS", progress });
  };

  mosaic.onUploadProgress = progress => {
    dispatch({ type: "SET_MOSAIC_UPLOAD_PROGRESS", progress });
  };

  mosaic.onError = error => {
    dispatch({ type: "MOSAIC_GENERATION_ERROR", error });
    if (error.message.lastIndexOf("Fail to read", 0) === 0) {
      dispatch(alert("error", error.message));
    } else {
      dispatch(
        alert(
          "error",
          "Sorry, something went wrong during the process. Please try again or contact us."
        )
      );
      logException(error, { event: "Error in loading mosaic tiles" });
    }
  };
};

// ================================= Target =================================
export const setMosaicTarget = source => async (dispatch, getState) => {
  if (getState().mosaic.target.loading) {
    // double click
    return;
  }
  reactGA.event({
    category: "User",
    action: `Added big photo`
  });
  dispatch({ type: "SET_MOSAIC_TARGET" });
  try {
    const {
      previewUrl,
      w,
      h,
      x,
      y,
      exifOrientation,
      aspectRatio
    } = await mosaic.setTarget(source);
    dispatch({
      type: "SET_MOSAIC_TARGET_SUCCESS",
      url: previewUrl,
      w,
      h,
      x,
      y,
      exifOrientation,
      aspectRatio,
      name: source.name
    });
  } catch (error) {
    dispatch({ type: "SET_MOSAIC_TARGET_FAILURE" });
    dispatch(alert("warning", `Fail to read image.`));
    logException(error, { event: "Error in loading mosaic target" });
  }
};
export const clearMosaicTarget = () => async dispatch => {
  reactGA.event({
    category: "User",
    action: `Cleared big photo`
  });
  dispatch({
    type: "CLEAR_MOSAIC_TARGET"
  });
  await mosaic.removeTarget();
};
export const enterMosaicTargetCopeMode = () => ({
  type: "ENTER_MOSAIC_TARGET_CROP_MODE",
  url: mosaic.getTargetSourceUrl()
});
export const leaveMosaicTargetCopeMode = () => dispatch => {
  mosaic.revokeTargetSourceUrl();
  dispatch({ type: "LEAVE_MOSAIC_TARGET_CROP_MODE" });
};
export const toggleMosaicTargetCropOrientation = () => ({
  type: "TOGGLE_MOSAIC_TARGET_CROP_ORIENTATION"
});
export const updateMosaicTargetCropRegion = region => ({
  type: "UPDATE_MOSAIC_TARGET_CROP_REGION",
  region
});
export const cropMosaicTarget = () => async (dispatch, getState) => {
  reactGA.event({
    category: "User",
    action: `Cropped big photo`
  });
  try {
    const {
      x: originalX,
      y: originalY,
      w: originalW,
      h: originalH,
      cropRegion
    } = getState().mosaic.target;
    const { x, y, w, h } = cropRegion;
    if (
      x === originalX &&
      y === originalY &&
      w === originalW &&
      h === originalH
    ) {
      dispatch(leaveMosaicTargetCopeMode());
      return;
    }
    dispatch({ type: "CROP_MOSAIC_TARGET" });
    const { previewUrl, aspectRatio } = await mosaic.cropTarget(x, y, w, h);
    dispatch({
      type: "CROP_MOSAIC_TARGET_SUCCESS",
      url: previewUrl,
      w,
      h,
      x,
      y,
      aspectRatio
    });
  } catch (error) {
    dispatch({ type: "CROP_MOSAIC_TARGET_FAILURE" });
    dispatch(
      alert(
        "warning",
        `Sorry, fail to crop image. Please try again or contact us.`
      )
    );
    logException(error, { event: "Error in cropping mosaic target" });
  } finally {
    mosaic.revokeTargetSourceUrl();
  }
};
export const saveMosaicTargetCope = () => ({
  type: "SAVE_MOSAIC_TARGET_CROP"
});

// ================================= Tiles ==================================

async function createJPEGFile(
  url,
  metadata = {
    type: "image/jpeg"
  }
) {
  let response = await fetch(url);
  let data = await response.blob();
  const fileName = last(url.split("/"));
  return new File([data], fileName, metadata);
}

export const addMosaicTiles = inputs => async (dispatch, getState) => {
  const files = (
    await Promise.all(
      inputs.map(input =>
        typeof input === "string"
          ? // it is a jpeg url
            createJPEGFile(input).catch(() => false)
          : // it is a jpeg url
            input
      )
    )
  ).filter(Boolean);

  reactGA.event({
    category: "User",
    action: `Added ${files.length} small photo(s)`
  });
  dispatch({ type: "START_ADDING_TILES" });

  const { tiles } = getState().mosaic;
  // Check duplication
  const newFiles = files.filter(file => {
    const id = file2Id(file);
    if (typeof tiles.byId[id] !== "undefined") return false;
    Object.defineProperty(file, "id", {
      value: id
    });
    return true;
  });
  dispatch({ type: "ADD_MOSAIC_TILES", files: newFiles });

  mosaic.onTileCached = id => {
    dispatch({ type: "MOSAIC_TILE_CACHED", id });
  };

  mosaic.addTileFiles(newFiles);
};

export const removeMosaicTile = id => dispatch => {
  reactGA.event({
    category: "User",
    action: `Removed a small photo`
  });
  dispatch({ type: "REMOVE_MOSAIC_TILE", id });
  mosaic.removeTile(id);
};
export const clearMosaicTiles = () => dispatch => {
  reactGA.event({
    category: "User",
    action: `Cleared all small photo(s)`
  });
  mosaic.onTileCached = id => {};

  mosaic.clearTiles();
  dispatch({ type: "CLEAR_MOSAIC_TILES" });
};
export const updateMosaicTilesFilter = filter => ({
  type: "UPDATE_MOSAIC_TILES_FILTER",
  filter
});
export const resetMosaicTilesFilter = () => ({
  type: "RESET_MOSAIC_TILES_FILER"
});

export const getMosaicTileCache = async id =>
  await mosaic.getTileFoto(id).getCache();

export const generateMosaic = () => (dispatch, getState) => {
  const t0 = performance.now();
  const state = getState();
  reactGA.event({
    category: "User",
    action: `Clicked generate mosaic with ${state.mosaic.tiles.count} small photos`
  });
  dispatch(push("/create/mosaic/generating"));
  dispatch({ type: "START_GENERATING_MOSAIC" });

  mosaic.onPreview = ({ url, width, height, nofTileOutput, isPortrait }) => {
    dispatch({
      type: "FINISH_GENERATING_MOSAIC",
      url,
      width,
      height,
      nofTileOutput,
      isPortrait
    });
    dispatch(push("/create/mosaic/step3"));

    const duration = ~~((performance.now() - t0) / 1000);
    reactGA.event({
      category: "Mosaic",
      action: `Generated mosaic in ${duration}s`
    });
  };

  mosaic.generate();
};

export const updateMosaicOverlay = overlay => async dispatch => {
  dispatch({ type: "UPDATE_MOSAIC_OVERLAY", overlay });
};

// Order
export const addItem = ({ productId, quantity, optionId }) => (
  dispatch,
  getState
) => {
  const state = getState();

  window.fbq("track", "AddToCart", {
    value: getPrice({ productId, optionId, state }),
    currency: state.locale.currency.toUpperCase(),
    content_ids: [`${productId}-${optionId}`],
    content_type: "product"
  });

  dispatch({
    type: "ADD_CARTITEM",
    productId,
    quantity,
    optionId
  });

  checkCart(dispatch, getState);
};
const checkCart = (dispatch, getState) => {
  const {
    products,
    cart: { cartItems }
  } = getState();
  let physicalProduct = false;
  let digitalProduct = false;
  let digitalProductCartItem;
  cartItems.forEach(({ productId, optionId }) => {
    if (products[productId].needShipping) {
      physicalProduct = true;
    } else {
      digitalProduct = true;
      digitalProductCartItem = { productId, optionId };
    }
  });
  if (physicalProduct && digitalProduct) {
    dispatch(removeCartItem(digitalProductCartItem));
    dispatch(
      alert(
        "success",
        "Softcopy is included in all our physical products. That's why we are removing the print-it-yourself version from your cart :D"
      )
    );
  }
};
export function changeOrderQuantity({ productId, optionId, quantity }) {
  return {
    type: "CHANGE_ORDER_QUANTITY",
    productId,
    optionId,
    quantity
  };
}
export function removeCartItem({ productId, optionId }) {
  return {
    type: "REMOVE_CARTITEM",
    productId,
    optionId
  };
}
export function clearCart() {
  return {
    type: "CLEAR_CART"
  };
}

// Coupon
export const changeCoupon = () => ({
  type: "CHANGE_COUPON"
});
export const removeCoupon = () => ({
  type: "REMOVE_COUPON"
});
export const verifyCoupon = code => async dispatch => {
  dispatch({ type: "VERIFY_COUPON" });
  try {
    const { valid, message, coupon } = await api.verifyCoupon(code);
    dispatch({
      type: "VERIFY_COUPON_SUCCESS",
      valid,
      message,
      coupon
    });
  } catch (error) {
    dispatch({ type: "VERIFY_COUPON_FAILURE" });
    logException(error, { event: "Error in verifying coupon" });
  }
};

// Shipping
export const getShippingMethods = countryCode => ({
  type: "GET_SHIPPING_METHODS",
  countryCode
});
export const selectShippingMethod = id => ({
  type: "SELECT_SHIPPING_METHOD",
  id
});

// checkout
export const goToCheckout = () => dispatch => {
  dispatch(push("/checkout"));
};
export const checkoutSuccess = () => ({
  type: "CHECKOUT_SUCCESS"
});

export const toggleCheckoutLoading = bool => ({
  type: "TOGGLE_CHECKOUT_LOADING",
  bool
});

// Products
export function gettingProducts(bool) {
  return {
    type: "GETTING_PRODUCTS",
    bool
  };
}

export function getProductsSuccess(products) {
  return {
    type: "GET_PRODUCTS_SUCCESS",
    products
  };
}

export function getProductsError(err) {
  return {
    type: "GET_PRODUCTS_ERROR",
    error: err
  };
}

export function getProducts() {
  return dispatch => {
    dispatch(gettingProducts(true));
    fetch("/.netlify/functions/products")
      .then(response => response.json())
      .then(products => dispatch(getProductsSuccess(products)))
      .catch(err => dispatch(getProductsError(err)));
  };
}

// Dialog
export const toggleDialog = (name, bool) => ({
  type: "TOGGLE_DIALOG",
  name,
  bool
});

// Drawer
export const toggleCart = bool => ({
  type: "TOGGLE_CART",
  bool
});

// Alert
export const alert = (variant, message) => ({
  type: "ALERT",
  variant,
  message
});

export const closeAlert = () => ({ type: "CLOSE_ALERT" });

// Other
export const getFreeCopySuccess = () => ({ type: "GET_FREE_COPY_SUCCESS" });



// WEBPACK FOOTER //
// ./src/actions/index.js